import { analytics } from "./firebase";

export type RegisterMethod = "email" | "google" | "apple" | "uae_pass";

/** GA4 rejects null params; strip null/undefined but keep "" and 0. */
const clean = (p: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v != null));

interface SearchTripArgs {
  origin: string;
  destination: string;
  departureDate: string;
  returnDate?: string;
  passengerType: string;
  passengerCount: number;
}
interface TicketContinueArgs {
  origin: string;
  travelDate: string;
  travelTime?: string;
  passengerType: string;
  trainClass: string;
  fare: string;
  passengerCount: number;
  price: number;
  currency?: string;
}
interface CheckoutPayArgs {
  paymentMethod: string;
  price: number;
  currency?: string;
  origin: string;
  destination: string;
  travelDate: string;
  travelTime?: string;
  trainNumber?: string;
  trainClass?: string;
  ticketOption?: string;
  passengerCount: number;
}
interface PaymentConfirmationArgs extends CheckoutPayArgs {
  bookingReference: string;
}
interface ChangeDateArgs {
  bookingReference: string;
  changeFees: number;
  price: number;
  currency?: string;
}
interface RoundTripArgs {
  bookingReference: string;
  newBookingReference: string;
  price: number;
  currency?: string;
  origin: string;
  destination: string;
  travelDate: string;
  fare?: string;
}

export const Analytics = {
  setUser(hashedUserId: string | null) {
    const g = analytics();
    if (!g || !hashedUserId) return;
    g("set", { user_id: hashedUserId });
  },

  // #1
  registerEmailAttempt() {
    const g = analytics();
    if (g) g("event", "keyclock_register_email");
  },

  // #2 (+ #3 when uae_pass)
  registerSuccess(method: RegisterMethod) {
    const g = analytics();
    if (!g) return;
    g("event", "keyclock_succs_register", { register_method: method });
    if (method === "uae_pass") {
      g("event", "keyclock_succs_register_uaepass", { register_method: "uae_pass" });
    }
  },

  // #4
  searchTrip(o: SearchTripArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "search_btn", clean({
      origin: o.origin,
      destination: o.destination,
      departure_date: o.departureDate,
      return_date: o.returnDate ?? "",
      passenger_type: o.passengerType,
      passenger_count: o.passengerCount,
    }));
  },

  // #5
  ticketOptionContinue(o: TicketContinueArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "ticket_opt_continue_btn", clean({
      origin: o.origin,
      travel_date: o.travelDate,
      travel_time: o.travelTime ?? "",
      passenger_type: o.passengerType,
      train_class: o.trainClass,
      fare: o.fare,
      passenger_count: o.passengerCount,
      price: o.price,
      currency: o.currency ?? "AED",
    }));
  },

  // #6
  checkoutPay(o: CheckoutPayArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "checkout_pay_btn", clean({
      payment_method: o.paymentMethod,
      price: o.price,
      currency: o.currency ?? "AED",
      origin: o.origin,
      destination: o.destination,
      travel_date: o.travelDate,
      travel_time: o.travelTime ?? "",
      train_number: o.trainNumber ?? "",
      train_class: o.trainClass ?? "",
      ticket_option: o.ticketOption ?? "",
      passenger_count: o.passengerCount,
    }));
  },

  // #7
  paymentConfirmation(o: PaymentConfirmationArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "payment_confirmation", clean({
      booking_reference: o.bookingReference,
      payment_method: o.paymentMethod,
      price: o.price,
      currency: o.currency ?? "AED",
      origin: o.origin,
      destination: o.destination,
      travel_date: o.travelDate,
      travel_time: o.travelTime ?? "",
      train_number: o.trainNumber ?? "",
      train_class: o.trainClass ?? "",
      ticket_option: o.ticketOption ?? "",
      passenger_count: o.passengerCount,
    }));
  },

  // #8
  changeDateConfirmation(o: ChangeDateArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "change_payment_confirmation", clean({
      booking_reference: o.bookingReference,
      change_fees: o.changeFees,
      price: o.price,
      currency: o.currency ?? "AED",
    }));
  },

  // #9
  roundTripPaymentConfirmation(o: RoundTripArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "book_roundtrip_payment_confirmation", clean({
      booking_reference: o.bookingReference,
      new_booking_reference: o.newBookingReference,
      price: o.price,
      currency: o.currency ?? "AED",
      origin: o.origin,
      destination: o.destination,
      travel_date: o.travelDate,
      fare: o.fare ?? "",
    }));
  },
};
